(() => {
  const initSpin = (root) => {
    if (!root || root.dataset.hhSpinReady === 'true') return;
    root.dataset.hhSpinReady = 'true';
    const viewer = root.querySelector('[data-hh-spin-viewer]');
    const frame = root.querySelector('[data-hh-spin-frame]');
    const progress = root.querySelector('[data-hh-spin-progress]');
    const sources = [...root.querySelectorAll('[data-hh-spin-source]')].map((item) => item.dataset.hhSpinSource).filter(Boolean);
    if (!viewer || !frame || sources.length < 2) { root.classList.add('is-static'); return; }
    let index = 0, startX = 0, startIndex = 0, dragging = false;
    const render = (next) => {
      index = (next + sources.length) % sources.length;
      frame.src = sources[index];
      if (progress) progress.style.width = `${((index + 1) / sources.length) * 100}%`;
    };
    viewer.addEventListener('pointerdown', (event) => { dragging = true; startX = event.clientX; startIndex = index; viewer.setPointerCapture(event.pointerId); });
    viewer.addEventListener('pointermove', (event) => { if (dragging) render(startIndex + Math.round((event.clientX - startX) / 16)); });
    viewer.addEventListener('pointerup', () => { dragging = false; });
    viewer.addEventListener('pointercancel', () => { dragging = false; });
    viewer.addEventListener('keydown', (event) => { if (event.key === 'ArrowRight') render(index + 1); if (event.key === 'ArrowLeft') render(index - 1); });
    render(0);
  };

  const initRail = (rail) => {
    const track = rail.querySelector('[data-hh-bundle-track]');
    const dotsWrap = rail.querySelector('[data-hh-bundle-dots]');
    const previous = rail.querySelector('[data-hh-bundle-prev]');
    const next = rail.querySelector('[data-hh-bundle-next]');
    if (!track || !dotsWrap) return;
    let pageSize = 1, pageCount = 1, ticking = false;

    const metrics = () => {
      const first = track.querySelector('[data-hh-bundle-product]');
      if (!first) return { step: track.clientWidth, visible: 1 };
      const style = window.getComputedStyle(track);
      const gap = Number.parseFloat(style.columnGap || style.gap || '0') || 0;
      const width = first.getBoundingClientRect().width;
      const step = Math.max(1, width + gap);
      const visible = Math.max(1, Math.floor((track.clientWidth + gap) / step));
      return { step, visible };
    };

    const activePage = () => {
      const { step } = metrics();
      return Math.max(0, Math.min(pageCount - 1, Math.round(track.scrollLeft / (step * pageSize))));
    };

    const syncControls = () => {
      const page = activePage();
      [...dotsWrap.querySelectorAll('button')].forEach((dot, index) => {
        dot.classList.toggle('is-active', index === page);
        dot.setAttribute('aria-current', index === page ? 'true' : 'false');
      });
      if (previous) previous.disabled = track.scrollLeft <= 3;
      if (next) next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 3;
    };

    const rebuild = () => {
      const items = [...track.querySelectorAll('[data-hh-bundle-product]')];
      const { visible } = metrics();
      pageSize = visible;
      pageCount = Math.max(1, Math.ceil(items.length / visible));
      dotsWrap.replaceChildren();
      if (pageCount <= 1) {
        rail.classList.add('is-static');
      } else {
        rail.classList.remove('is-static');
        for (let index = 0; index < pageCount; index += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.setAttribute('aria-label', `Page ${index + 1}`);
          dot.addEventListener('click', () => {
            const { step } = metrics();
            track.scrollTo({ left: index * pageSize * step, behavior: 'smooth' });
          });
          dotsWrap.append(dot);
        }
      }
      syncControls();
    };

    const move = (direction) => {
      const { step } = metrics();
      track.scrollBy({ left: direction * pageSize * step, behavior: 'smooth' });
    };

    previous?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));
    track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { syncControls(); ticking = false; });
    }, { passive: true });

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(rebuild);
      observer.observe(track);
    } else {
      window.addEventListener('resize', rebuild, { passive: true });
    }
    rebuild();
  };

  const renderUpdatedCart = async (cart, result) => {
    if (!cart || typeof cart.getSectionsToRender !== 'function') return;

    const requested = cart.getSectionsToRender();
    const ids = requested.map((section) => section.id);
    const currentSections = result?.sections || {};
    const hasAllSections = ids.every(
      (id) => typeof currentSections[id] === 'string' && currentSections[id]
    );

    if (hasAllSections) {
      cart.renderContents(result);
      return;
    }

    // Shopify can return a null rendered section even when the cart mutation
    // succeeded. Refresh only the cart UI instead of reloading the page.
    const rootUrl = window.Shopify?.routes?.root || '/';
    const cartUrl = window.routes?.cart_url || `${rootUrl}cart`;
    const separator = cartUrl.includes('?') ? '&' : '?';
    const response = await fetch(
      `${cartUrl}${separator}sections=${encodeURIComponent(ids.join(','))}`,
      {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
      }
    );

    if (!response.ok) throw new Error('Cart sections refresh failed');

    const sections = await response.json();
    cart.renderContents({ ...result, sections });
  };

  const initBundle = (root) => {
    if (!root || root.dataset.hhBundleReady === 'true') return;
    root.dataset.hhBundleReady = 'true';
    const size = Number(root.dataset.hhBundleSize || 3);
    const currency = root.dataset.hhCurrency || 'EUR';
    const choices = [...root.querySelectorAll('[data-hh-bundle-choice]')];
    const slots = [...root.querySelectorAll('[data-hh-bundle-slot]')];
    const submit = root.querySelector('[data-hh-bundle-submit]');
    const footer = root.querySelector('[data-hh-bundle-footer]');
    const total = root.querySelector('[data-hh-bundle-total]');
    const status = root.querySelector('[data-hh-bundle-status]');
    const workspace = root.querySelector('.hh-bundle-builder__workspace');
    const sizeRequiredText = workspace?.dataset.sizeRequired || 'Choose a size for every pair';
    const removeLabel = workspace?.dataset.removeLabel || 'Remove pair';
    const submitLabel = submit?.dataset.label || submit?.textContent?.trim() || 'Add set to bag';
    let picked = [];

    root.querySelectorAll('[data-hh-bundle-rail]').forEach(initRail);

    const getChoice = (productId) => choices.find((item) => item.dataset.productId === productId);
    const getWrap = (productId) => root.querySelector(`[data-hh-bundle-product][data-product-id="${productId}"]`);
    const getVariantId = (productId) => {
      const choice = getChoice(productId);
      const select = getWrap(productId)?.querySelector('[data-hh-bundle-variant]');
      return select ? select.value : choice?.dataset.defaultVariantId || '';
    };
    const getPrice = (productId) => {
      const choice = getChoice(productId);
      const select = getWrap(productId)?.querySelector('[data-hh-bundle-variant]');
      if (select) {
        const option = select.options[select.selectedIndex];
        return Number(option?.dataset.price || 0);
      }
      return Number(choice?.dataset.defaultPrice || 0);
    };
    const money = (cents) => {
      try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100); }
      catch (error) { return `${(cents / 100).toFixed(2)} ${currency}`; }
    };

    const sync = () => {
      choices.forEach((choice) => choice.setAttribute('aria-pressed', picked.includes(choice.dataset.productId) ? 'true' : 'false'));
      slots.forEach((slot, index) => {
        const productId = picked[index];
        const choice = getChoice(productId);
        slot.replaceChildren();
        if (choice) {
          const image = document.createElement('img');
          const info = document.createElement('div');
          const title = document.createElement('span');
          const sourceSelect = getWrap(productId)?.querySelector('[data-hh-bundle-variant]');
          const remove = document.createElement('button');
          image.src = choice.dataset.image; image.alt = '';
          title.textContent = choice.dataset.title;
          info.className = 'hh-bundle-builder__slot-info';
          remove.className = 'hh-bundle-builder__slot-remove';
          remove.type = 'button';
          remove.setAttribute('aria-label', `${removeLabel}: ${choice.dataset.title}`);
          remove.textContent = '×';
          remove.addEventListener('click', () => {
            if (sourceSelect) sourceSelect.value = '';
            picked = picked.filter((item) => item !== productId);
            sync();
          });
          info.append(title);
          if (sourceSelect) {
            const slotSelect = sourceSelect.cloneNode(true);
            slotSelect.removeAttribute('data-hh-bundle-variant');
            slotSelect.className = 'hh-bundle-builder__slot-select';
            slotSelect.value = sourceSelect.value;
            slotSelect.addEventListener('change', () => { sourceSelect.value = slotSelect.value; sync(); });
            info.append(slotSelect);
          }
          slot.append(image, remove, info);
        } else {
          const number = document.createElement('b');
          number.textContent = index + 1;
          slot.append(number);
        }
      });

      const sizesReady = picked.length === size && picked.every((productId) => getVariantId(productId));
      const needsSize = picked.length === size && !sizesReady;
      const totalCents = sizesReady ? picked.reduce((sum, productId) => sum + getPrice(productId), 0) : 0;
      if (submit) {
        submit.disabled = !sizesReady;
        submit.textContent = sizesReady ? `${submitLabel} · ${money(totalCents)}` : submitLabel;
      }
      if (footer) footer.hidden = picked.length !== size;
      if (total) total.textContent = sizesReady ? money(totalCents) : '';
      if (status) {
        status.classList.toggle('is-size-required', needsSize);
        status.textContent = picked.length === size
          ? (sizesReady ? status.dataset.ready : sizeRequiredText)
          : status.dataset.remaining.replace('__COUNT__', size - picked.length);
      }
    };

    choices.forEach((choice) => choice.addEventListener('click', () => {
      const productId = choice.dataset.productId;
      if (picked.includes(productId)) {
        picked = picked.filter((item) => item !== productId);
        const select = getWrap(productId)?.querySelector('[data-hh-bundle-variant]');
        if (select) select.value = '';
      } else if (picked.length < size) {
        picked = [...picked, productId];
      }
      sync();
    }));
    root.querySelectorAll('[data-hh-bundle-variant]').forEach((select) => select.addEventListener('change', sync));

    if (submit) submit.addEventListener('click', async () => {
      const variantIds = picked.map(getVariantId);
      if (picked.length !== size || variantIds.some((id) => !id)) return;
      submit.disabled = true;
      try {
        const cart = document.querySelector('cart-drawer');
        const requestBody = { items: variantIds.map((id) => ({ id: Number(id), quantity: 1, properties: { '_HH set': '3 pair set' } })) };
        if (cart) {
          requestBody.sections = cart.getSectionsToRender().map((section) => section.id);
          requestBody.sections_url = window.location.pathname;
          cart.setActiveElement(submit);
        }
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(requestBody),
        });
        if (!response.ok) throw new Error('Cart request failed');
        const result = await response.json();

        if (cart) {
          await renderUpdatedCart(cart, result);
        }

        document.dispatchEvent(new CustomEvent('hh:cart-updated', {
          detail: { source: 'bundle-builder' },
        }));
        window.HHCartFeedback?.refresh();

        picked = [];
        root.querySelectorAll('[data-hh-bundle-variant]').forEach((select) => { select.value = ''; });
        sync();
      } catch (error) {
        submit.disabled = false;
        submit.textContent = submitLabel;
        if (status) status.textContent = submit.dataset.error;
      }
    });
    sync();
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-hh-spin]').forEach(initSpin);
    scope.querySelectorAll('[data-hh-bundle]').forEach(initBundle);
  };
  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
